import React, { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { getDashboardUser } from '../../store/dashboardUser/actionCreators';
import { DashboardUserStore } from '../../store/dashboardUser/dashboardUserTypes';
import { RootState } from '../../store';
import { UserRoles } from '../../shared/constants';
import { getFacilityRoleById, getPrivilegeById } from '../../shared/utils';
import LoadingSpinner from '../Shared/LoadingSpinner';

interface Props {
  element: React.ReactElement;
}

/**
 * Restricts a route to the ENGINEERING privilege. `ProtectedRoute` only blocks
 * the `isRestrictedRole` set (it lets customer_care / facility admins through),
 * so engineer-only pages need this stricter gate. Renders a spinner while the
 * user object is still loading (avoids a redirect flash on reload).
 */
const EngineerOnlyRoute: React.FC<Props> = ({ element }) => {
  const dispatch =
    useDispatch<ThunkDispatch<DashboardUserStore, void, AnyAction>>();
  const { dashboardUserInfo, error } = useSelector(
    (state: RootState) => state.dashboardUser,
  );

  // Deep-link/hard-reload lands here before anything has fetched the user;
  // without this the spinner never resolves.
  useEffect(() => {
    if (!dashboardUserInfo && !error) {
      dispatch(getDashboardUser());
    }
  }, [dashboardUserInfo, error, dispatch]);

  if (!dashboardUserInfo) {
    if (error) {
      return <Navigate to="/" replace />;
    }
    return <LoadingSpinner />;
  }

  const role = dashboardUserInfo.facility_role_id
    ? getFacilityRoleById(dashboardUserInfo.facility_role_id)
    : getPrivilegeById(dashboardUserInfo.privilege_id ?? 0);

  if (role !== UserRoles.ENGINEERING) {
    return <Navigate to="/" replace />;
  }

  return element;
};

export default EngineerOnlyRoute;
